import {
  SourceType,
  ExternalReferenceRemoveInput,
  ExternalReferenceAddInput,
} from 'graphql-query-contracts';

export type ExternalReference = {
  id: string;
  direct: boolean;
  source: {
    sourceType: SourceType;
    sourceEntityId: string;
    sourceEntityType: string;
  };
  relationshipSource?: {
    sourceType: SourceType;
    sourceRelationshipType: string;
    sourceRelationshipId: string;
  } | null;
};

export type ExternalReferenceDifferences = {
  referencesToAdd: ExternalReferenceAddInput[];
  referencesToRemove: ExternalReferenceRemoveInput[];
};

/**
 * Calculates the differences between existing and new external references
 * to determine which references need to be added or removed.
 *
 * @param existingReferences - Array of current external references (may contain nulls)
 * @param newReferences - Array of new external references to be set
 * @returns Object containing references to add and remove
 */
export const calculateExternalReferenceDifferences = (
  existingReferences: Array<ExternalReference>,
  newReferences: Array<ExternalReference>
): ExternalReferenceDifferences => {
  // References to add: new references that don't exist in current
  const referencesToAdd = newReferences
    .filter(
      newRef =>
        !existingReferences.some(existingRef =>
          referenceIsEqual(newRef, existingRef ?? undefined)
        )
    )
    .map(ref => ({
      sourceType: ref.source.sourceType,
      sourceEntityId: ref.source.sourceEntityId,
    }));

  // References to remove: existing references that aren't in new
  const referencesToRemove: ExternalReferenceRemoveInput[] = existingReferences
    .filter(
      existingRef =>
        existingRef &&
        !newReferences.some(newRef =>
          referenceIsEqual(newRef, existingRef ?? undefined)
        )
    )
    .map(ref => ({
      source: {
        sourceEntityId: ref.source.sourceEntityId,
        sourceEntityType: ref.source.sourceEntityType,
        sourceType: ref.source.sourceType,
      },
      relationshipSource: ref.relationshipSource
        ? {
            sourceType: ref.relationshipSource.sourceType,
            sourceRelationshipType:
              ref.relationshipSource.sourceRelationshipType,
            sourceRelationshipId: ref.relationshipSource.sourceRelationshipId,
          }
        : undefined,
    }));

  return { referencesToAdd, referencesToRemove };
};

const referenceIsEqual = (
  ref1: ExternalReference,
  ref2?: ExternalReference
) => {
  return (
    ref1.source.sourceType === ref2?.source.sourceType &&
    ref1.source.sourceEntityId === ref2?.source.sourceEntityId &&
    ref1.source.sourceEntityType === ref2?.source.sourceEntityType
  );
};
